import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import HelpLayout from '../../../components/help/HelpLayout';
import { 
  LightBulbIcon,
  ChartBarIcon,
  AcademicCapIcon,
  UserIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const CareerGuidanceHelp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'User Guide', href: '/help/getting-started' },
    { name: 'Career Guidance' }
  ];

  const assessmentSteps = [
    {
      title: 'Skills Assessment',
      description: 'Evaluate your current technical and soft skills',
      icon: CheckCircleIcon,
      details: [
        'Rate your proficiency in various technical skills',
        'Assess your soft skills like communication and teamwork',
        'Identify areas for improvement and development',
        'Get personalized skill gap analysis'
      ]
    },
    {
      title: 'Interest Evaluation',
      description: 'Discover your career interests and preferences',
      icon: StarIcon,
      details: [
        'Explore different career fields and industries',
        'Identify work environments that suit you',
        'Understand your work style preferences',
        'Match interests with career opportunities'
      ]
    },
    {
      title: 'Goal Setting',
      description: 'Define your career objectives and timeline',
      icon: ChartBarIcon,
      details: [
        'Set short-term and long-term career goals',
        'Define your preferred learning timeline',
        'Consider location and lifestyle preferences',
        'Establish realistic career milestones'
      ]
    }
  ];

  const recommendationTypes = [
    {
      title: 'Course Recommendations',
      description: 'Personalized training program suggestions based on your assessment',
      features: [
        'Skill-matched course suggestions',
        'Career pathway alignment',
        'Difficulty level matching',
        'Location-based recommendations'
      ]
    },
    {
      title: 'Career Path Guidance',
      description: 'Structured pathways to achieve your career goals',
      features: [
        'Step-by-step career progression',
        'Required qualifications mapping',
        'Industry insights and trends',
        'Salary and growth projections'
      ]
    },
    {
      title: 'Skill Development Plan',
      description: 'Targeted plan to bridge your skill gaps',
      features: [
        'Priority skill identification',
        'Learning resource recommendations',
        'Progress tracking tools',
        'Milestone achievement rewards'
      ]
    }
  ];

  return (
    <HelpLayout title="Career Guidance" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        {/* Introduction */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Career Guidance System
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '24px'
            }}>
              GMSP3 Career Guidance provides personalized recommendations to help you
              make informed decisions about your education and career path. Using advanced matching 
              algorithms, we analyze your skills, interests, and goals to suggest the most suitable 
              training programs and career opportunities.
            </p>

            {/* Quick Start */}
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--primary-yellow-light)',
              borderRadius: '8px',
              border: '1px solid var(--primary-yellow)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🚀 Quick Start
              </h3>
              <p style={{ color: 'var(--text-gray)', margin: 0, lineHeight: 1.6 }}>
                Navigate to <strong>Career Guidance</strong> from the main menu to start your personalized 
                assessment. Complete all sections for the most accurate recommendations.
              </p>
            </div>
          </div>
        </div>

        {/* Assessment Process */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              📊 Assessment Process
            </h3>

            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '24px'
            }}>
              The career assessment consists of three main components that work together to create 
              your personalized profile:
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              {assessmentSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} style={{
                    padding: '24px',
                    border: '1px solid var(--border-gray)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--white)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <Icon style={{
                        width: '24px',
                        height: '24px',
                        color: 'var(--primary-yellow)'
                      }} />
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: 'var(--primary-black)',
                        margin: 0
                      }}>
                        {step.title}
                      </h4>
                    </div>
                    <p style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      lineHeight: 1.6,
                      marginBottom: '16px'
                    }}>
                      {step.description}
                    </p>
                    <ul style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      lineHeight: 1.6,
                      paddingLeft: '16px',
                      margin: 0
                    }}>
                      {step.details.map((detail, idx) => (
                        <li key={idx}>{detail}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* How to Complete Assessment */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              ✅ How to Complete Your Assessment
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                {
                  step: '1',
                  title: 'Start the Assessment',
                  description: 'Click "Start Assessment" on the Career Guidance page to begin your personalized evaluation.'
                },
                {
                  step: '2',
                  title: 'Skills Section',
                  description: 'Rate your current skill levels honestly. This helps us understand your strengths and areas for development.'
                },
                {
                  step: '3',
                  title: 'Interests Section',
                  description: 'Select career fields and work environments that interest you. Be open to exploring new areas.'
                },
                {
                  step: '4',
                  title: 'Preferences Section',
                  description: 'Specify your learning preferences, timeline, and location constraints for accurate matching.'
                },
                {
                  step: '5',
                  title: 'Review & Submit',
                  description: 'Review your responses and submit to generate your personalized recommendations.'
                }
              ].map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  gap: '16px',
                  padding: '16px',
                  backgroundColor: 'var(--background-gray)',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: 'var(--primary-yellow)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: 'var(--white)',
                    flexShrink: 0
                  }}>
                    {item.step}
                  </div>
                  <div>
                    <h5 style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: 'var(--primary-black)',
                      marginBottom: '4px'
                    }}>
                      {item.title}
                    </h5>
                    <p style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      lineHeight: 1.5,
                      margin: 0
                    }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Understanding Recommendations */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              🎯 Understanding Your Recommendations
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              {recommendationTypes.map((type, index) => (
                <div key={index} style={{
                  padding: '20px',
                  border: '1px solid var(--border-gray)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--white)'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: 'var(--primary-black)',
                    marginBottom: '12px'
                  }}>
                    {type.title}
                  </h4>
                  <p style={{
                    fontSize: '14px',
                    color: 'var(--text-gray)',
                    lineHeight: 1.6,
                    marginBottom: '16px'
                  }}>
                    {type.description}
                  </p>
                  <ul style={{
                    fontSize: '14px',
                    color: 'var(--text-gray)',
                    lineHeight: 1.6,
                    paddingLeft: '16px',
                    margin: 0
                  }}>
                    {type.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Match Scores */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              📈 Understanding Match Scores
            </h3>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '16px'
            }}>
              Each recommendation comes with a match score that indicates how well it aligns with your profile:
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              {[
                { range: '90-100%', label: 'Excellent Match', color: '#10B981', description: 'Highly recommended for you' },
                { range: '80-89%', label: 'Good Match', color: '#F59E0B', description: 'Strong alignment with your profile' },
                { range: '70-79%', label: 'Fair Match', color: '#6B7280', description: 'Some alignment, worth considering' },
                { range: '60-69%', label: 'Partial Match', color: '#9CA3AF', description: 'Limited alignment' }
              ].map((score, index) => (
                <div key={index} style={{
                  padding: '16px',
                  backgroundColor: 'var(--background-gray)',
                  borderRadius: '6px',
                  borderLeft: `4px solid ${score.color}`
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: score.color,
                    marginBottom: '4px'
                  }}>
                    {score.range}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: 'var(--primary-black)',
                    marginBottom: '4px'
                  }}>
                    {score.label}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-gray)'
                  }}>
                    {score.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Retaking Assessment */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <ArrowPathIcon style={{ width: '24px', height: '24px', color: 'var(--primary-yellow)' }} />
              Updating Your Assessment
            </h3>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '16px'
            }}>
              You can retake your career assessment at any time to get updated recommendations. Consider retaking when:
            </p>
            <ul style={{
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              paddingLeft: '20px',
              marginBottom: '16px'
            }}>
              <li>You've completed new training or gained new skills</li>
              <li>Your career interests or goals have changed</li>
              <li>You want to explore different career paths</li>
              <li>Your location or availability preferences have changed</li>
            </ul>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-gray)',
              fontStyle: 'italic'
            }}>
              <strong>Note:</strong> Your previous assessment results will be saved, and you can compare 
              your progress over time.
            </p>
          </div>
        </div>

        {/* Next Steps */}
        <div className="card">
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              🎯 Next Steps
            </h3>
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <Link to="/career-guidance" className="btn btn-primary">
                Start Assessment
              </Link>
              <Link to="/courses" className="btn btn-outline">
                Browse Courses
              </Link>
              <Link to="/opportunities" className="btn btn-outline">
                Explore Opportunities
              </Link>
            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default CareerGuidanceHelp;
